import React from "react";
import { X } from "lucide-react";
import type { TooltipRenderProps } from "react-joyride";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

/**
 * Joyride tooltip styled to match ToolKit dashboard cards and shadcn-style buttons.
 */
export const DashboardTourTooltip: React.FC<TooltipRenderProps> = ({
  backProps,
  closeProps,
  index,
  isLastStep,
  primaryProps,
  skipProps,
  size,
  step,
  tooltipProps,
}) => {
  const { content, title } = step;
  const closeLabel =
    typeof closeProps["aria-label"] === "string"
      ? closeProps["aria-label"]
      : "Close tour";

  const { children: _closeChildren, ...closeButtonProps } =
    closeProps as typeof closeProps & {
      children?: React.ReactNode;
    };

  const showFooter =
    step.buttons?.some(
      (b) => b === "back" || b === "primary" || b === "skip",
    ) ?? false;

  return (
    <div
      className={cn(
        "react-joyride__tooltip flex max-w-[min(420px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl",
        "border border-border bg-card text-left text-card-foreground shadow-card",
        "font-sans antialiased",
      )}
      data-joyride-step={index}
      {...(step.id ? { "data-joyride-id": step.id } : {})}
      {...tooltipProps}
      {...(title
        ? {
            "aria-labelledby": "dashboard-tour-title",
            "aria-describedby": "dashboard-tour-content",
          }
        : {
            "aria-label": closeLabel,
            "aria-describedby": "dashboard-tour-content",
          })}
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 pr-1 pt-0.5 text-[11px] font-bold uppercase tracking-wider text-premium-600 leading-snug">
            Step {index + 1} of {size}
          </p>
          <button
            type="button"
            className={cn(
              "inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg",
              "text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
            )}
            {...closeButtonProps}
          >
            <X className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        </div>

        {title ? (
          <h4
            id="dashboard-tour-title"
            className="mt-4 font-display text-lg font-semibold tracking-tight text-foreground"
          >
            {title}
          </h4>
        ) : null}
        <div
          id="dashboard-tour-content"
          className={cn(
            "text-sm leading-relaxed text-muted-foreground",
            title ? "mt-3" : "mt-4",
          )}
        >
          {content}
        </div>
      </div>

      {showFooter ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-4">
          {step.buttons?.includes("skip") && !isLastStep ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mr-auto text-muted-foreground hover:text-foreground"
              {...skipProps}
            />
          ) : (
            <span className="mr-auto" aria-hidden />
          )}
          {step.buttons?.includes("back") && index > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-border"
              {...backProps}
            />
          ) : null}
          {step.buttons?.includes("primary") ? (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="bg-primary text-primary-foreground shadow-sm"
              {...primaryProps}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
